import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import { 
  Users, 
  UserPlus, 
  Search,
  Filter,
  Calendar,
  BookOpen,
  Target,
  CheckCircle,
  Clock,
  MoreVertical,
  Edit,
  Trash2,
  Mail,
  Key,
  Eye,
  EyeOff,
  Save,
  X,
  AlertCircle
} from 'lucide-react';

interface User {
  id: string;
  name: string;
  email: string;
  roles: string[];
  createdAt: string;
  updatedAt: string;
}

interface CourseAssignment {
  id: string;
  userId: string;
  tutorialId?: string;
  categoryId?: string;
  assignedBy: string;
  dueDate?: string;
  isCompleted: boolean;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface Tutorial {
  id: string;
  title: string;
  category: string;
  difficulty: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resettingUser, setResettingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const isAdmin = user?.roles?.includes('admin');
  const isManager = user?.roles?.includes('manager');

  useEffect(() => {
    // Only admins and managers can access user management
    if (isAdmin || isManager) {
      fetchUsers(1, searchTerm, selectedRole);
      fetchAssignments();
      fetchTutorials();
      fetchCategories();
    }
  }, [isAdmin, isManager]);

  useEffect(() => {
    // Allow all authenticated users to fetch users
    fetchUsers(currentPage, searchTerm, selectedRole);
  }, [currentPage, searchTerm, selectedRole]);

  const fetchUsers = async (page = 1, search = '', role = '') => {
    try {
      setLoading(true);
      const response = await apiService.getUsers({
        page,
        limit: 10,
        search: search || undefined,
        role: role !== 'all' ? role : undefined
      });
      
      if (response.success && response.data) {
        setUsers(response.data.users || []);
        setPagination(response.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
      }
    } catch (err) {
      console.error('Users fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      // Use the all assignments endpoint for admin/manager users
      const endpoint = (isAdmin || isManager) ? '/user-management/assignments/all' : '/user-management/assignments';
      const response = await apiService.request(endpoint);
      setAssignments(response.data || []);
    } catch (err) {
      console.error('Assignments fetch error:', err);
    }
  };

  const fetchTutorials = async () => {
    try {
      const response = await apiService.getTutorials();
      setTutorials(response.tutorials || []);
    } catch (err) {
      console.error('Tutorials fetch error:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.request('/categories');
      setCategories(response.data || []);
    } catch (err) {
      console.error('Categories fetch error:', err);
    }
  };

  const createAssignment = async (assignmentData: any) => {
    try {
      const response = await apiService.request('/user-management/assignments', {
        method: 'POST',
        body: JSON.stringify(assignmentData)
      });

      // Refresh assignments and show confirmation
      await fetchAssignments();
      setAssignments(prev => prev); // trigger render
      // optimistic update: reflect count immediately
      // Optionally show a toast confirmation if you prefer
      console.log('Assignment created');
      setShowAssignmentModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Assignment creation error:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to create assignment');
    }
  };

  const createUser = async (userData: any) => {
    try {
      const response = await apiService.request('/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      // Refresh users
      fetchUsers(currentPage, searchTerm, selectedRole);
      setShowCreateUserModal(false);
    } catch (err: any) {
      console.error('User creation error:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to create user');
    }
  };

  const updateUser = async (userId: string, userData: any) => {
    try {
      await apiService.updateUser(userId, userData);
      
      // Refresh users
      fetchUsers(currentPage, searchTerm, selectedRole);
      setShowEditUserModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error('User update error:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      
      // Refresh users
      fetchUsers(currentPage, searchTerm, selectedRole);
    } catch (err) {
      console.error('User deletion error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const resetPassword = async (userId: string, newPassword: string) => {
    try {
      await apiService.resetPassword(userId, newPassword);
      alert('Password reset successfully');
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    }
  };

  const getRoleColor = (roles: string[]) => {
    if (roles.includes('admin')) return 'text-purple-600 bg-purple-100';
    if (roles.includes('manager')) return 'text-blue-600 bg-blue-100';
    if (roles.includes('instructor')) return 'text-green-600 bg-green-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getRoleIcon = (roles: string[]) => {
    if (roles.includes('admin')) return '👑';
    if (roles.includes('manager')) return '👨‍💼';
    if (roles.includes('instructor')) return '👨‍🏫';
    return '👤';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || user.roles.includes(selectedRole);
    
    return matchesSearch && matchesRole;
  });

  // Check if user has access to user management
  if (!isAdmin && !isManager) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need admin or manager privileges to access user management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => {
              setError(null);
              fetchUsers();
            }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">
            Manage users, assign courses, and track progress
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="instructor">Instructor</option>
                <option value="learner">Learner</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-end">
              <button 
                onClick={() => setShowCreateUserModal(true)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Users ({filteredUsers.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const userAssignments = assignments.filter(a => a.userId === user.id);
                  const completedAssignments = userAssignments.filter(a => a.isCompleted).length;
                  
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((role, index) => (
                            <span
                              key={index}
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor([role])}`}
                            >
                              {getRoleIcon([role])} {role}
                            </span>
                          ))}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {completedAssignments} / {userAssignments.length} completed
                        </div>
                        {userAssignments.length > 0 && (
                          <div className="text-xs text-gray-500">
                            {Math.round((completedAssignments / userAssignments.length) * 100)}% completion rate
                          </div>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ 
                                width: `${userAssignments.length > 0 ? (completedAssignments / userAssignments.length) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">
                            {userAssignments.length > 0 ? Math.round((completedAssignments / userAssignments.length) * 100) : 0}%
                          </span>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowAssignmentModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Assign Course"
                          >
                            <Target className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setResettingUser(user);
                              setShowResetPasswordModal(true);
                            }}
                            className="text-gray-600 hover:text-gray-900"
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setShowEditUserModal(true);
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Edit User"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete User"
                            disabled={user.id === user?.id}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <button
                            className="text-gray-600 hover:text-gray-900"
                            title="Send Email"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create User Modal */}
        {showCreateUserModal && (
          <CreateUserModal
            onClose={() => setShowCreateUserModal(false)}
            onCreate={createUser}
          />
        )}

        {/* Edit User Modal */}
        {showEditUserModal && editingUser && (
          <EditUserModal
            user={editingUser}
            onClose={() => {
              setShowEditUserModal(false);
              setEditingUser(null);
            }}
            onUpdate={updateUser}
          />
        )}

        {/* Assignment Modal */}
        {showAssignmentModal && selectedUser && (
          <AssignmentModal
            user={selectedUser}
            tutorials={tutorials}
            categories={categories}
            onClose={() => {
              setShowAssignmentModal(false);
              setSelectedUser(null);
            }}
            onAssign={async (data) => {
              await createAssignment(data);
              // refresh user list so counts update
              fetchUsers(currentPage, searchTerm, selectedRole);
            }}
          />
        )}

        {/* Reset Password Modal */}
        {showResetPasswordModal && resettingUser && (
          <ResetPasswordModal
            user={resettingUser}
            onClose={() => {
              setShowResetPasswordModal(false);
              setResettingUser(null);
            }}
            onReset={async (newPwd: string) => {
              await resetPassword(resettingUser.id, newPwd);
              setShowResetPasswordModal(false);
              setResettingUser(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Assignment Modal Component
interface AssignmentModalProps {
  user: User;
  tutorials: Tutorial[];
  categories: Category[];
  onClose: () => void;
  onAssign: (data: any) => void;
}

// Reset Password Modal
interface ResetPasswordModalProps {
  user: User;
  onClose: () => void;
  onReset: (newPassword: string) => void | Promise<void>;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ user, onClose, onReset }) => {
  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd.length < 6) {
      setErrorMsg('Password must be at least 6 characters long');
      return;
    }
    if (pwd !== confirmPwd) {
      setErrorMsg('Passwords do not match');
      return;
    }
    setErrorMsg(null);
    setIsSubmitting(true);
    try {
      await onReset(pwd);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">Reset Password for {user.name}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
            </div>
            {errorMsg && <div className="mb-3 text-sm text-red-600">{errorMsg}</div>}
            <div className="flex justify-end space-x-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{isSubmitting ? 'Saving...' : 'Reset Password'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const AssignmentModal: React.FC<AssignmentModalProps> = ({ user, tutorials, categories, onClose, onAssign }) => {
  const [tab, setTab] = useState<'tutorials' | 'categories'>('tutorials');
  const [query, setQuery] = useState('');
  const [difficulty, setDifficulty] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [visibleCount, setVisibleCount] = useState(20);

  const toggleId = (id: string) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filteredTutorials = tutorials
    .filter(t => (difficulty === 'all' || t.difficulty === difficulty))
    .filter(t => (categoryFilter === 'all' || t.category === categoryFilter))
    .filter(t => t.title.toLowerCase().includes(query.toLowerCase()));

  const filteredCategories = categories.filter(c => c.name.toLowerCase().includes(query.toLowerCase()));

  const handleAssign = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIds.length === 0) return;
    selectedIds.forEach(id => {
      const payload = tab === 'tutorials'
        ? { userId: user.id, tutorialId: id, dueDate: dueDate || undefined }
        : { userId: user.id, categoryId: id, dueDate: dueDate || undefined };
      onAssign(payload);
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-16 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Assign content to {user.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="flex space-x-2 mb-4">
          <button onClick={() => setTab('tutorials')} className={`px-3 py-1.5 rounded ${tab === 'tutorials' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Tutorials</button>
          <button onClick={() => setTab('categories')} className={`px-3 py-1.5 rounded ${tab === 'categories' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Categories</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input type="text" placeholder={tab === 'tutorials' ? 'Search tutorials...' : 'Search categories...'} value={query} onChange={(e) => setQuery(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          {tab === 'tutorials' && (
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value as any)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="all">All difficulties</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          )}
          {tab === 'tutorials' && (
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="all">All categories</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          )}
        </div>

        <div className="max-h-80 overflow-auto border rounded-lg">
          {tab === 'tutorials' ? (
            <ul>
              {filteredTutorials.slice(0, visibleCount).map(t => (
                <li key={t.id} className="flex items-center justify-between px-4 py-2 border-b last:border-b-0 hover:bg-gray-50">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" checked={selectedIds.includes(t.id)} onChange={() => toggleId(t.id)} />
                    <span>
                      <span className="font-medium text-gray-900">{t.title}</span>
                      <span className="ml-2 text-xs text-gray-500 capitalize">{t.difficulty}</span>
                      <span className="ml-2 text-xs text-gray-500">{t.category}</span>
                    </span>
                  </label>
                </li>
              ))}
              {filteredTutorials.length === 0 && (
                <li className="px-4 py-6 text-center text-gray-500">No tutorials match your filters</li>
              )}
            </ul>
          ) : (
            <ul>
              {filteredCategories.slice(0, visibleCount).map(c => (
                <li key={c.id} className="flex items-center justify-between px-4 py-2 border-b last:border-b-0 hover:bg-gray-50">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleId(c.id)} />
                    <span className="font-medium text-gray-900">{c.name}</span>
                  </label>
                </li>
              ))}
              {filteredCategories.length === 0 && (
                <li className="px-4 py-6 text-center text-gray-500">No categories match your search</li>
              )}
            </ul>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm text-gray-600">Selected: <span className="font-medium">{selectedIds.length}</span></div>
          {((tab === 'tutorials' && filteredTutorials.length > visibleCount) || (tab === 'categories' && filteredCategories.length > visibleCount)) && (
            <button onClick={() => setVisibleCount(c => c + 20)} className="text-sm text-blue-600 hover:text-blue-800">Load more</button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Due date (optional)</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div className="flex items-end justify-end space-x-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleAssign} disabled={selectedIds.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Assign {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create User Modal Component
interface CreateUserModalProps {
  onClose: () => void;
  onCreate: (data: any) => void;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  onClose,
  onCreate
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: ['learner']
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate(formData);
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        roles: [...prev.roles, role]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        roles: prev.roles.filter(r => r !== role)
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Create New User</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roles
              </label>
              <div className="space-y-2">
                {['admin', 'manager', 'instructor', 'learner'].map((role) => (
                  <label key={role} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.roles.includes(role)}
                      onChange={(e) => handleRoleChange(role, e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 capitalize">{role}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit User Modal Component
interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onUpdate: (userId: string, data: any) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  onClose,
  onUpdate
}) => {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    roles: user.roles
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(user.id, formData);
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        roles: [...prev.roles, role]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        roles: prev.roles.filter(r => r !== role)
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit User</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roles
              </label>
              <div className="space-y-2">
                {['admin', 'manager', 'instructor', 'learner'].map((role) => (
                  <label key={role} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.roles.includes(role)}
                      onChange={(e) => handleRoleChange(role, e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 capitalize">{role}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
